import { useQuery } from "@apollo/client";
import { GET_GITHUB_STATS } from "../graphql/queries/github";

export interface GitHubStatsData {
  profile: {
    totalCommits: number;
    totalPRs: number;
    totalIssues: number;
    totalReleases: number;
    contributionsThisYear: number;
    longestStreak: number;
    currentStreak: number;
    publicRepos: number;
    privateRepos: number;
  };
  languages: Array<{
    name: string;
    percentage: number;
    commits: number;
    color?: string;
  }>;
  repositories: Array<{
    id: string;
    name: string;
    fullName: string;
    description: string | null;
    language: string | null;
    stars: number;
    forks: number;
    watchers: number;
    commits: number;
    pullRequests: number;
    issues: number;
    releases: number;
    isPrivate: boolean;
    htmlUrl: string;
    updatedAt: string;
    pushedAt: string | null;
    lastUpdated?: string;
  }>;
  activity: Array<{
    date: string;
    commits: number;
  }>;
  hourlyActivity: Array<{
    hour: number;
    dayOfWeek: number;
    commits: number;
  }>;
  lastSyncAt: string | null;
  lastSyncSuccess: boolean;
}

// Language colors mapping (GitHub standard colors)
const LANGUAGE_COLORS: Record<string, string> = {
  TypeScript: "#3178c6",
  JavaScript: "#f7df1e",
  Python: "#3776ab",
  Java: "#b07219",
  "C#": "#178600",
  "C++": "#f34b7d",
  C: "#555555",
  Go: "#00ADD8",
  Rust: "#dea584",
  Ruby: "#701516",
  PHP: "#4F5D95",
  Swift: "#ffac45",
  Kotlin: "#A97BFF",
  Dart: "#00B4AB",
  Shell: "#89e051",
  HTML: "#e34c26",
  CSS: "#563d7c",
  SCSS: "#c6538c",
  Vue: "#41b883",
  React: "#61dafb",
  Unknown: "#6c757d",
};

export function useGitHubStats(useMockData: boolean = false) {
  const {
    data: queryData,
    loading,
    error,
    refetch,
  } = useQuery(GET_GITHUB_STATS, {
    skip: useMockData,
    fetchPolicy: "no-cache", // Changed from "cache-and-network" to force fresh data
  });

  // Transform the data to add colors and format dates
  const data: GitHubStatsData | null = queryData?.githubStats
    ? {
        ...queryData.githubStats,
        languages: queryData.githubStats.languages.map((lang: any) => ({
          ...lang,
          color: LANGUAGE_COLORS[lang.name] || LANGUAGE_COLORS.Unknown,
        })),
        repositories: queryData.githubStats.repositories.map((repo: any) => ({
          ...repo,
          lastUpdated: new Date(repo.updatedAt).toLocaleDateString(),
        })),
      }
    : null;

  const refreshData = async () => {
    if (!useMockData) {
      await refetch();
    }
  };

  return {
    data,
    loading,
    error: error?.message || null,
    refreshData,
  };
}
